import { hash } from "daedalus-core";
import { GAME_EPOCH_MS, TURN_MS, turnOf } from "../time/game-clock.ts";
import { STORM_KIND, regionWord, weatherAt, type WeatherKind, type WeatherTable } from "./weather.ts";

/**
 * How weather looks on a page (ADR-144 decision A4): from the kind of weather and the time into the turn, how much rain is
 * drawn, how dark the sky gets and when lightning flashes. Pure and seeded only by the world seed, the region and the clock,
 * so every page in a region shows the same turn-over and the same flashes.
 */

/**
 * How long the weather takes to turn over at the start of a turn, in milliseconds (ADR-144 decision A4: the first 30 s).
 */
export const WEATHER_BLEND_MS = 30_000;

/**
 * How many strips of screen-wide emitters a light rain and a heavy rain fill. A strip is one pooled emitter across a
 * share of the screen's width; these are the density knobs.
 */
export const LIGHT_RAIN_STRIPS = 14;
export const HEAVY_RAIN_STRIPS = 24;

/**
 * The sky's dim in rain and in a storm, 0 to 1 of black laid over the world.
 */
export const RAIN_DIM = 0.14;
export const STORM_DIM = 0.3;

/**
 * Lightning: a window in which one flash may fall, the share of storm windows that flash in tenths of a percent, the length
 * of a flash and its peak whiteness.
 */
export const FLASH_WINDOW_MS = 9000;
export const FLASH_PER_THOUSAND = 400;
export const FLASH_LENGTH_MS = 420;
export const FLASH_PEAK = 0.5;

const FLASH_DOMAIN = 0x6c696768;
const FLASH_SECOND_START_MS = 200;
const FLASH_FIRST_MS = 140;
const FLASH_SECOND_MS = 220;
const FLASH_SECOND_SHARE = 0.6;
const FLASH_RISE_MS = 30;

/**
 * What the page draws: how many strips of each rain, and the dim.
 */
export interface RainLook {
  readonly lightStrips: number;
  readonly heavyStrips: number;
  readonly dim: number;
}

/**
 * A `RainLook` a caller reuses frame after frame, so reading the look allocates nothing.
 */
export type MutableRainLook = { -readonly [K in keyof RainLook]: RainLook[K] };

/**
 * The level of a kind of weather: 0 clear, 1 rain, 2 storm.
 *
 * @param kind The kind.
 * @returns Its level.
 */
function levelOf(kind: WeatherKind): number {
  return kind === "clear" ? 0 : kind === STORM_KIND ? 2 : 1;
}

/**
 * How much weather is falling now: the previous turn's level moving evenly to this turn's over the first `WEATHER_BLEND_MS`
 * of the turn.
 *
 * @param previous The kind of the turn before.
 * @param current The kind of this turn.
 * @param msIntoTurn Milliseconds since this turn began; below 0 reads as 0.
 * @returns A strength from 0 (clear) through 1 (rain) to 2 (storm).
 */
export function weatherStrength(previous: WeatherKind, current: WeatherKind, msIntoTurn: number): number {
  const from = levelOf(previous);
  const to = levelOf(current);
  const share = msIntoTurn <= 0 ? 0 : msIntoTurn >= WEATHER_BLEND_MS ? 1 : msIntoTurn / WEATHER_BLEND_MS;
  return from + (to - from) * share;
}

/**
 * Milliseconds since a turn began.
 *
 * @param epochMs The server-clock time.
 * @param turn The turn it falls in (`turnOf`).
 * @returns The time into the turn.
 */
function msIntoTurn(epochMs: number, turn: number): number {
  return epochMs - GAME_EPOCH_MS - turn * TURN_MS;
}

/**
 * How much weather is falling in a region at a server-clock time: this turn's kind against the turn before it, blended over the
 * start of the turn. Every page computes it from the same inputs, so the weather turns over together.
 *
 * @param table The weather table.
 * @param worldSeed The world's seed.
 * @param regionId The region id (`weatherRegionOf`).
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns A strength from 0 to 2 (`weatherStrength`).
 */
export function weatherStrengthAt(table: WeatherTable, worldSeed: number, regionId: string, epochMs: number): number {
  const turn = turnOf(epochMs);
  const current = weatherAt(table, worldSeed, regionId, turn).kind;
  const previous = turn > 0 ? weatherAt(table, worldSeed, regionId, turn - 1).kind : current;
  return weatherStrength(previous, current, msIntoTurn(epochMs, turn));
}

/**
 * The same strength as `weatherStrengthAt`, for a caller that asks every frame: the kinds of a turn are worked out once, when
 * the turn changes, so a frame in the same turn does arithmetic only and allocates nothing.
 *
 * @param table The weather table.
 * @param worldSeed The world's seed.
 * @param regionId The region id (`weatherRegionOf`).
 * @returns A function from a server-clock time in milliseconds to a strength from 0 to 2.
 */
export function weatherStrengthSource(table: WeatherTable, worldSeed: number, regionId: string): (epochMs: number) => number {
  let cachedTurn = -1;
  let previous: WeatherKind = "clear";
  let current: WeatherKind = "clear";
  return (epochMs) => {
    const turn = turnOf(epochMs);
    if (turn !== cachedTurn) {
      cachedTurn = turn;
      current = weatherAt(table, worldSeed, regionId, turn).kind;
      previous = turn > 0 ? weatherAt(table, worldSeed, regionId, turn - 1).kind : current;
    }
    return weatherStrength(previous, current, msIntoTurn(epochMs, turn));
  };
}

/**
 * What a strength draws, written into a look the caller owns: light rain fills up to strength 1, then thins as heavy rain
 * fills toward strength 2; the dim rises with it.
 *
 * @param strength From `weatherStrength`.
 * @param out The look to write.
 * @returns `out`.
 */
export function rainLookInto(strength: number, out: MutableRainLook): MutableRainLook {
  const s = strength <= 0 ? 0 : strength >= 2 ? 2 : strength;
  if (s <= 1) {
    out.lightStrips = Math.round(LIGHT_RAIN_STRIPS * s);
    out.heavyStrips = 0;
    out.dim = RAIN_DIM * s;
    return out;
  }
  const over = s - 1;
  out.lightStrips = Math.round(LIGHT_RAIN_STRIPS * (1 - over));
  out.heavyStrips = Math.round(HEAVY_RAIN_STRIPS * over);
  out.dim = RAIN_DIM + (STORM_DIM - RAIN_DIM) * over;
  return out;
}

/**
 * What a strength draws.
 *
 * @param strength From `weatherStrength`.
 * @returns The strip counts and the dim.
 */
export function rainLook(strength: number): RainLook {
  return rainLookInto(strength, { lightStrips: 0, heavyStrips: 0, dim: 0 });
}

/**
 * One pulse of a flash: a quick rise and a linear fall to nothing.
 *
 * @param t Milliseconds since the pulse began.
 * @param length The pulse's length.
 * @returns 0 to 1.
 */
function pulse(t: number, length: number): number {
  if (t <= 0 || t >= length) return 0;
  return t < FLASH_RISE_MS ? t / FLASH_RISE_MS : 1 - (t - FLASH_RISE_MS) / (length - FLASH_RISE_MS);
}

/**
 * The whiteness of lightning at a time: in a storm, each window of `FLASH_WINDOW_MS` flashes or not and, if it does, when,
 * from the hash of the world seed, the region and the window; a flash is a strong pulse and a weaker one just after.
 *
 * @param worldSeed The world's seed.
 * @param regionId The region id (`weatherRegionOf`).
 * @param epochMs The server-clock time in milliseconds since the Unix epoch.
 * @param strength From `weatherStrength`; only a storm (above 1.5) flashes.
 * @returns White laid over the world, 0 to `FLASH_PEAK`.
 */
export function stormFlashAt(worldSeed: number, regionId: string, epochMs: number, strength: number): number {
  if (strength <= 1.5) return 0;
  const window = Math.floor(epochMs / FLASH_WINDOW_MS) >>> 0;
  const region = regionWord(regionId);
  const draw = hash(worldSeed, region, FLASH_DOMAIN, window);
  if ((draw >>> 8) % 1000 >= FLASH_PER_THOUSAND) return 0;
  const start = hash(worldSeed, region, FLASH_DOMAIN + 1, window) % (FLASH_WINDOW_MS - FLASH_LENGTH_MS);
  const t = epochMs - window * FLASH_WINDOW_MS - start;
  const level = Math.max(pulse(t, FLASH_FIRST_MS), FLASH_SECOND_SHARE * pulse(t - FLASH_SECOND_START_MS, FLASH_SECOND_MS));
  return level * FLASH_PEAK;
}
